'use client'
import { Label } from "@/components/ui/label";
import { ChevronDownIcon, Bell, TrendingUp, Clock, Sparkles } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { SimplePicker } from "@/components/ui/searchPicker";
import { CurrencyOption } from "@/types/calculator";
import { useState, useEffect, Suspense } from "react";
import Flag from "@/components/ui/flag";
import { useCurrencyRate } from "@/hooks/use-currency-rate";
import { changeAmountFormat } from "@/components/helper/number";
import { Input } from "@/components/ui/input";
import { useTranslation } from "@/components/language/hooks/useTranslation";

function CurrencyFromQuery({ onMatch }: { onMatch: (code: string | null) => void }) {
  const searchParams = useSearchParams();
  const preselectedCurrency = searchParams.get("currency");

  useEffect(() => {
    onMatch(preselectedCurrency);
  }, [preselectedCurrency, onMatch]);

  return null;
}

function RateTrackerContent() {
  const [preselectedCurrency, setPreselectedCurrency] = useState<string | null>(null);
  const [defaultOptions, setDefaultOptions] = useState<CurrencyOption[]>([]);
  const [open, setOpen] = useState(false);
  const { t, tm, ti } = useTranslation();
  const router = useRouter();
  const [targetRate, setTargetRate] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyOption>({
    currency_code: "USD",
    currency_name: "US Dollar",
    country_name: "United States",
    code_alpha2: "us",
  });
  const [appliedPreselect, setAppliedPreselect] = useState(false);

  if (!appliedPreselect && preselectedCurrency && defaultOptions.length > 0) {
    setAppliedPreselect(true);
    const match = defaultOptions.find((c) => c.currency_code === preselectedCurrency);
    if (match) setSelectedCurrency(match);
  }

  const { data: rateData, isLoading } = useCurrencyRate({
    from: "AUD",
    to: selectedCurrency.currency_code,
    amount: 500,
  });

  useEffect(() => {
    fetch("/api/home/compare/currencies")
      .then((res) => {
        if (!res.ok) return { success: false, data: [] };
        return res.json();
      })
      .then((json: any) => {
        let currencies: any[] = [];
        if (json?.success && json?.data) currencies = Array.isArray(json.data) ? json.data : [];
        else if (Array.isArray(json)) currencies = json;
        else if (Array.isArray(json?.data)) currencies = json.data;

        const list: CurrencyOption[] = currencies
          .filter((c: any) => c && c.is_receiving_enabled)
          .map((c: any) => ({
            currency_code: c.currency_code,
            currency_name: c.currency_name,
            country_name: c.country_name || c.currency_name,
            code_alpha2: String(c.code_alpha2 || "").toLowerCase(),
            useCurrencyFlag: false,
            id: c.id,
            min: c.min_transfer_amount,
            symbol: c.currency_symbol,
            max: c.max_transfer_amount,
          }))
          .sort((a: CurrencyOption, b: CurrencyOption) =>
            a.currency_code.localeCompare(b.currency_code),
          );

        setDefaultOptions(list);
      })
      .catch(() => setDefaultOptions([]));
  }, []);

  const redirectToLogin = () => router.push("/login?redirect=tracker");

  const rateValue = rateData?.rate ? changeAmountFormat(rateData.rate, 0, 4) : "—";
  const isFormValid = !!targetRate && !!validUntil;

  return (
    <div className="bg-brand-bg flex items-center justify-center px-4 py-12 lg:py-24 relative overflow-hidden">
      <Suspense fallback={null}>
        <CurrencyFromQuery onMatch={setPreselectedCurrency} />
      </Suspense>

      <div className="relative w-full max-w-md mx-auto 3xl:max-w-[1432px]">
        {/* Header */}
        <div className="mb-10 text-center">
          <h1
            className="mb-3 text-3xl sm:text-5xl 3xl:text-6xl font-bold tracking-tight text-brand-text 3xl:text-8xl"
          >
            {t("rate.title")}{" "}
          </h1>
          <p className="text-lg 3xl:text-2xl 3xl:max-w-2xl mx-auto leading-relaxed text-brand-text">
            {t("rate.subtitle")}
          </p>
        </div>

        <div className="relative global-border-radius border border-brand-grey/10 bg-brand-card overflow-hidden mx-auto 3xl:max-w-lg">
          <div className="flex flex-col gap-6 p-7">
            <div className="flex flex-col gap-2">
              <Label className="text-xs 3xl:text-sm font-semibold tracking-widest uppercase text-brand-text">
                {t("rate.recipientCurrency")}{" "}
              </Label>
              <SimplePicker<CurrencyOption>
                items={(defaultOptions as any) ?? []}
                value={selectedCurrency}
                searchable
                searchPlaceholder={t("rate.searchCurrency")}
                filter={(c, kw) => {
                  const lower = kw.toLowerCase();
                  return (
                    (typeof c.currency_name === "string" &&
                      c.currency_name.toLowerCase().includes(lower)) ||
                    (typeof c.currency_code === "string" &&
                      c.currency_code.toLowerCase().includes(lower))
                  );
                }}
                onChange={(item) => {
                  setSelectedCurrency(item);
                  setOpen(!open);
                }}
                renderTrigger={(value) => (
                  <button
                    type="button"
                    className="flex items-center justify-between w-full gap-2 px-4 py-3 text-sm 3xl:text-base font-normal global-border-radius border border-white/10 bg-white/[0.04] hover:bg-white/[0.07] transition-all duration-200 focus:outline-none focus:border-brand-warn"
                  >
                    {value ? (
                      <span className="flex items-center gap-3">
                        <span className="overflow-hidden">
                          <Flag code={value.code_alpha2.toLowerCase()} width={22} height={22} circle={true} />
                        </span>
                        <span className="text-sm 3xl:text-base font-semibold text-brand-text/80">{value.currency_code}</span>
                        <span className="text-xs 3xl:text-sm text-brand-text/80">{value.currency_name}</span>
                      </span>
                    ) : (
                      <span className="text-brand-card">{t("rate.selectCurrency")}</span>
                    )}
                    <ChevronDownIcon className="flex-shrink-0 w-4 h-4 text-brand-text/80" />
                  </button>
                )}
                renderItem={(item, selected) => (
                  <div
                    className={`px-4 py-2.5 flex justify-between items-center cursor-pointer transition-colors ${selected ? "bg-orange-500/10" : "hover:bg-white/5"}`}
                  >
                    <span className="flex items-center gap-3">
                      <span className="overflow-hidden rounded">
                        <Flag code={item.code_alpha2.toLowerCase()} width={22} height={22} circle={true} />
                      </span>
                      <span className="text-sm 3xl:text-base font-semibold text-brand-text/80">{item.currency_code}</span>
                      <span className="text-xs 3xl:text-sm truncate text-brand-text/80">{item.currency_name}</span>
                    </span>
                    {selected && <div className="h-1.5 w-1.5 rounded-full bg-brand-warn/20" />}
                  </div>
                )}
              />
            </div>

            {/* Live Rate Display */}
            <div className="relative global-border-radius border border-brand-card bg-brand-grey/30 p-5 overflow-hidden">
              <div className="flex items-start justify-between relative">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="h-3.5 w-3.5 text-brand-warn" />
                    <span className="text-xs 3xl:text-sm font-semibold tracking-widest uppercase text-brand-warn">
                      Live Rate
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span
                      className="text-3xl 3xl:text-5xl font-bold tracking-tight text-brand-text"
                    >
                      {isLoading ? (
                        <span className="inline-block h-8 global-border-radius w-28 bg-white/10 animate-pulse" />
                      ) : (
                        rateValue
                      )}
                    </span>
                    <span className="text-sm 3xl:text-lg font-medium text-brand-warn">
                      {selectedCurrency.currency_code}
                    </span>
                  </div>
                  <p className="text-sm 3xl:text-lg text-brand-text/80 mt-1.5">{t("rate.perAud")}</p>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-white/[0.06]" />
              <div className="flex items-center gap-1.5">
                <Bell className="w-3 h-3 text-brand-text/80" />
                <span className="text-xs 3xl:text-sm font-semibold tracking-widest uppercase text-brand-text/80">
                  {t("rate.alert")}
                </span>
              </div>
              <div className="flex-1 h-px bg-white/[0.06]" />
            </div>

            <div className="flex flex-col gap-2">
              <Label
                className="text-xs 3xl:text-sm font-semibold tracking-widest uppercase text-brand-text 3xl:text-xl"
                htmlFor="target_rate"
              >
                {t("rate.targetRate")}{" "}
              </Label>
              <div className="relative">
                <Input
                  value={targetRate}
                  onChange={(e) => setTargetRate(e.target.value)}
                  id="target_rate"
                  type="number"
                  step="any"
                  placeholder="e.g. 0.70"
                  className="w-full global-border-radius border border-white/10 bg-white/[0.04] px-4 py-3 text-white placeholder:text-brand-text focus:border-brand-warn focus:ring-0 focus:outline-none transition-all duration-200 text-base sm:text-sm 3xl:text-base [color-scheme:dark]"
                />
                <span className="absolute text-xs 3xl:text-lg font-semibold -translate-y-1/2 right-4 top-1/2 text-brand-text/80">
                  {selectedCurrency.currency_code}
                </span>
              </div>
              <p className="text-xs 3xl:text-sm text-brand-text leading-relaxed">
                Notify me when 1 AUD reaches this value in {selectedCurrency.currency_code}.
              </p>
            </div>

            {/* Valid Until Input */}
            <div className="flex flex-col gap-2">
              <Label
                className="text-xs 3xl:text-sm font-semibold tracking-widest uppercase text-brand-text"
                htmlFor="expiry_date"
              >
                Alert expired on
              </Label>
              <div className="relative w-full overflow-hidden">
                <Clock className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none left-4 top-1/2 text-brand-text" />
                {/* {!validUntil && (
                  <span className="absolute left-11 top-1/2 -translate-y-1/2 text-slate-600 text-base sm:text-sm pointer-events-none select-none">
                    {t("rate.selectDate")}
                  </span>
                )} */}
                <Input
                  value={validUntil}
                  onChange={(e) => setValidUntil(e.target.value)}
                  type="date"
                  id="expiry_date"
                  className="w-full appearance-none global-border-radius border border-white/10 bg-white/[0.04] pl-11 pr-4 py-3 text-white focus:border-orange-500/50 focus:ring-0 focus:outline-none transition-all duration-200 text-base sm:text-sm [color-scheme:dark]"
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              disabled={!isFormValid}
              onClick={redirectToLogin}
              type="submit"
              className={`relative w-full py-3.5 global-border-radius font-semibold text-sm 3xl:text-lg transition-all overflow-hidden group
                ${isFormValid
                  ? "bg-brand-grey hover:-translate-y-0.5"
                  : "bg-white/[0.05] text-brand-text border border-white/[0.06] cursor-not-allowed"
                }`}
            >
              {isFormValid && (
                <span className="absolute inset-0 bg-gradient-to-r from-orange-400/0 via-white/10 to-orange-400/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
              )}
              <span className="relative flex items-center justify-center gap-2">
                <Bell className="w-4 h-4" />
                {t("rate.setAlert")}{" "}
              </span>
            </button>

            <p className="text-center text-xs 3xl:text-sm text-brand-text">{t("rate.loginPrompt")} </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RateTracker() {
  return <RateTrackerContent />;
}
